import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import {
  Cog6ToothIcon,
  BoltIcon,
  BellIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import HomePortSettings from './HomePortSettings';

const Container = styled.div`
  max-width: 900px;
`;

const Section = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  margin-bottom: 1.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  margin: 0.5rem 0 0 0;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const SettingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.text.secondary};
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.input};
  color: ${theme.colors.text.primary};
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 1px ${theme.colors.primary.main};
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.input};
  color: ${theme.colors.text.primary};
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 1px ${theme.colors.primary.main};
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${theme.colors.primary.dark};
  }

  &:disabled {
    background: ${theme.colors.neutral[500]};
    cursor: not-allowed;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.colors.text.secondary};
`;

const TripPreferences = () => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await api.get('/api/trip-preferences');
      console.log('🔍 FETCH - Raw response:', response.data);
      
      // Backend returns { preferences: {...} }, so extract the preferences object
      const prefs = response.data.preferences || response.data;
      console.log('🔍 FETCH - Extracted preferences:', prefs);
      
      setPreferences(prefs);
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggleChange = (field) => {
    setPreferences(prev => ({
      ...prev,
      // Treat missing/undefined as ON for email prefs (opt-out defaults).
      [field]: prev[field] === false,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      console.log('🔧 SAVE - Original preferences object:', preferences);
      
      // Only send fields that the backend accepts (exclude id, user_id, created_at, updated_at, etc.)
      const {
        id,
        user_id,
        created_at,
        updated_at,
        preferences: prefsData, // In case the response is wrapped
        ...validPreferences
      } = preferences;
      
      console.log('🔧 SAVE - Valid preferences to send:', validPreferences);
      console.log('🔧 SAVE - Excluded fields:', { id, user_id, created_at, updated_at });
      
      const response = await api.put('/api/trip-preferences', validPreferences);
      console.log('✅ SAVE - Response:', response.data);
      
      // Update local state with the saved preferences from backend response
      if (response.data.preferences) {
        console.log('🔄 SAVE - Updating local state with backend response');
        setPreferences(response.data.preferences);
      }
      
      toast.success('All preferences saved successfully');
    } catch (error) {
      console.error('❌ SAVE ERROR - Full error:', error);
      console.error('❌ SAVE ERROR - Response:', error.response);
      console.error('❌ SAVE ERROR - Data:', error.response?.data);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Reset all preferences to defaults?')) {
      try {
        setSaving(true);
        const response = await api.post('/api/trip-preferences/reset');
        setPreferences(response.data);
        toast.success('Preferences reset to defaults');
      } catch (error) {
        console.error('Error resetting preferences:', error);
        toast.error('Failed to reset preferences');
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return <LoadingState>Loading preferences...</LoadingState>;
  }

  if (!preferences) {
    return <LoadingState>No preferences found</LoadingState>;
  }

  return (
    <Container>
      <HomePortSettings
        autoTripEnabled={preferences.auto_trip_enabled !== false}
        onAutoTripChange={(enabled) => handleInputChange('auto_trip_enabled', enabled)}
      />

      {/* Display Units */}
      <Section>
        <SectionHeader>
          <ChartBarIcon className="w-6 h-6" style={{ color: theme.colors.primary.main }} />
          <div>
            <SectionTitle>Display Units</SectionTitle>
            <SectionDescription>Choose your preferred units for displaying trip data</SectionDescription>
          </div>
        </SectionHeader>
        
        <SettingsGrid>
          <SettingItem>
            <Label>Distance Units</Label>
            <Select
              value={preferences.distance_unit || 'mi'}
              onChange={(e) => handleInputChange('distance_unit', e.target.value)}
            >
              <option value="nautical_miles">Nautical Miles</option>
              <option value="kilometers">Kilometers</option>
              <option value="statute_miles">Statute Miles</option>
            </Select>
          </SettingItem>

          <SettingItem>
            <Label>Fuel Units</Label>
            <Select
              value={preferences.fuel_units || 'liters'}
              onChange={(e) => handleInputChange('fuel_units', e.target.value)}
            >
              <option value="liters">Liters</option>
              <option value="gallons">Gallons (US)</option>
              <option value="imperial_gallons">Imperial Gallons</option>
            </Select>
          </SettingItem>

          <SettingItem>
            <Label>Speed Units</Label>
            <Select
              value={preferences.speed_unit || 'kts'}
              onChange={(e) => handleInputChange('speed_unit', e.target.value)}
            >
              <option value="knots">Knots</option>
              <option value="kmh">Km/h</option>
              <option value="mph">Mph</option>
            </Select>
          </SettingItem>

          <SettingItem>
            <Label>Temperature Units</Label>
            <Select
              value={preferences.temperature_units || 'celsius'}
              onChange={(e) => handleInputChange('temperature_units', e.target.value)}
            >
              <option value="celsius">Celsius</option>
              <option value="fahrenheit">Fahrenheit</option>
            </Select>
          </SettingItem>
        </SettingsGrid>
      </Section>


      {/* Notifications */}
      <Section>
        <SectionHeader>
          <BellIcon className="w-6 h-6" style={{ color: theme.colors.primary.main }} />
          <div>
            <SectionTitle>Notifications</SectionTitle>
            <SectionDescription>
              We email you when a trip starts, when you settle for the night away from home, and when you&apos;re back at the slip — so you always know we&apos;re watching the boat.
            </SectionDescription>
          </div>
        </SectionHeader>
        
        <SettingsGrid>
          <SettingItem>
            <Label>
              <input
                type="checkbox"
                checked={preferences.email_trip_started !== false}
                onChange={() => handleToggleChange('email_trip_started')}
                style={{ marginRight: '0.5rem' }}
              />
              Email when a trip starts
            </Label>
          </SettingItem>

          <SettingItem>
            <Label>
              <input
                type="checkbox"
                checked={preferences.email_trip_completed !== false}
                onChange={() => handleToggleChange('email_trip_completed')}
                style={{ marginRight: '0.5rem' }}
              />
              Email full trip summary when it ends
            </Label>
          </SettingItem>

          <SettingItem>
            <Label>
              <input
                type="checkbox"
                checked={preferences.email_health_alerts !== false}
                onChange={() => handleToggleChange('email_health_alerts')}
                style={{ marginRight: '0.5rem' }}
              />
              Include health alerts in trip emails
            </Label>
          </SettingItem>

          <SettingItem>
            <Label>
              <input
                type="checkbox"
                checked={preferences.email_daily_summary !== false}
                onChange={() => handleToggleChange('email_daily_summary')}
                style={{ marginRight: '0.5rem' }}
              />
              Email daily wrap-up on multi-day trips (skipped if you didn&apos;t move)
            </Label>
          </SettingItem>
        </SettingsGrid>
      </Section>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
        <Button onClick={handleReset} disabled={saving}>
          Reset to Defaults
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </Container>
  );
};

export default TripPreferences;